import { Injectable, signal, computed } from '@angular/core';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  readonly isIos = signal<boolean>(false);
  readonly isStandalone = signal<boolean>(false);
  readonly canPromptInstall = signal<boolean>(false);

  readonly canInstall = computed(
    () => !this.isStandalone() && (this.canPromptInstall() || this.isIos()),
  );

  constructor() {
    this.isIos.set(this.detectIos());
    this.isStandalone.set(this.detectStandalone());

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.canPromptInstall.set(true);
    });

    window.addEventListener('appinstalled', () => {
      this.canPromptInstall.set(false);
      this.isStandalone.set(true);
      this.deferredPrompt = null;
    });
  }

  async installPwa(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!this.deferredPrompt) {
      return 'unavailable';
    }

    await this.deferredPrompt.prompt();
    const { outcome } = await this.deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      this.canPromptInstall.set(false);
    }
    this.deferredPrompt = null;
    return outcome;
  }

  private detectIos(): boolean {
    const ua = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua);
    const isIpadOs =
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return isIosDevice || isIpadOs;
  }

  private detectStandalone(): boolean {
    const displayModeStandalone = window.matchMedia(
      '(display-mode: standalone)',
    ).matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    return displayModeStandalone || iosStandalone;
  }
}
